import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/settings/code-templates")({
	component: CodeTemplatesPage,
});

const aiClient = createClient(AIService, transport);

function CodeTemplatesPage() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: "Шаблоны кода" },
	]);

	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [showCreate, setShowCreate] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["code-templates"],
		queryFn: async () => {
			const res = await aiClient.listCodeTemplates({});
			return res.templates;
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => aiClient.deleteCodeTemplate({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["code-templates"] });
			toast.success("Шаблон удалён");
		},
	});

	const searchLower = search.toLowerCase();
	const templates = search
		? (data ?? []).filter(
				(tmpl) =>
					tmpl.name.toLowerCase().includes(searchLower) ||
					tmpl.description.toLowerCase().includes(searchLower),
			)
		: (data ?? []);

	return (
		<div className="space-y-4">
			{/* Toolbar */}
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						ref={searchRef}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Поиск шаблонов..."
						className="pl-9 h-9"
					/>
				</div>
				<div className="ml-auto">
					<Button size="sm" onClick={() => setShowCreate(true)}>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						Добавить
					</Button>
				</div>
			</div>

			{/* Table */}
			{isLoading ? (
				<div className="flex justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : templates.length === 0 ? (
				<div className="text-center py-12 text-sm text-muted-foreground">
					{search ? "Ничего не найдено" : "Нет шаблонов"}
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Название</TableHead>
							<TableHead>Репозиторий</TableHead>
							<TableHead>Ветка</TableHead>
							<TableHead className="w-[100px]">Тип</TableHead>
							<TableHead className="w-[50px]" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{templates.map((tmpl) => (
							<TableRow key={tmpl.id}>
								<TableCell>
									<div>
										<span className="font-medium text-sm">{tmpl.name}</span>
										{tmpl.description && (
											<p className="text-xs text-muted-foreground truncate max-w-[250px]">
												{tmpl.description}
											</p>
										)}
										{tmpl.tags.length > 0 && (
											<div className="flex gap-1 mt-1">
												{tmpl.tags.map((tag) => (
													<span
														key={tag}
														className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground"
													>
														{tag}
													</span>
												))}
											</div>
										)}
									</div>
								</TableCell>
								<TableCell>
									<a
										href={tmpl.gitUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
									>
										{tmpl.gitUrl
											.replace(/https?:\/\/(github\.com\/)?/, "")
											.slice(0, 40)}
										<ExternalLink className="h-3 w-3 shrink-0" />
									</a>
								</TableCell>
								<TableCell className="text-sm text-muted-foreground">
									{tmpl.gitBranch || "main"}
								</TableCell>
								<TableCell>
									{tmpl.isSystem ? (
										<Badge variant="secondary" className="text-[10px]">
											системный
										</Badge>
									) : (
										<Badge variant="outline" className="text-[10px]">
											свой
										</Badge>
									)}
								</TableCell>
								<TableCell>
									{!tmpl.isSystem && (
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-muted-foreground hover:text-destructive"
											onClick={() => deleteMut.mutate(tmpl.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<CreateTemplateDialog
				open={showCreate}
				onClose={() => setShowCreate(false)}
				onCreated={() => {
					qc.invalidateQueries({ queryKey: ["code-templates"] });
					setShowCreate(false);
					toast.success("Шаблон добавлен");
				}}
			/>
		</div>
	);
}

function CreateTemplateDialog({
	open,
	onClose,
	onCreated,
}: {
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
}) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [gitUrl, setGitUrl] = useState("");
	const [gitBranch, setGitBranch] = useState("main");

	const mutation = useMutation({
		mutationFn: () =>
			aiClient.createCodeTemplate({ name, description, gitUrl, gitBranch }),
		onSuccess: () => {
			setName("");
			setDescription("");
			setGitUrl("");
			setGitBranch("main");
			onCreated();
		},
	});

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Добавить шаблон</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label className="text-sm">Название</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Интернет-магазин (Next.js)"
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">Описание</Label>
						<Textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Полноценный магазин с каталогом и корзиной"
							rows={2}
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">Git URL</Label>
						<Input
							value={gitUrl}
							onChange={(e) => setGitUrl(e.target.value)}
							placeholder="https://github.com/org/repo"
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">Ветка</Label>
						<Input
							value={gitBranch}
							onChange={(e) => setGitBranch(e.target.value)}
							placeholder="main"
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Отмена
					</Button>
					<Button
						onClick={() => mutation.mutate()}
						disabled={!name || !gitUrl || mutation.isPending}
					>
						{mutation.isPending ? "Добавление..." : "Добавить"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
